import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarCheck, ChevronLeft, ChevronRight, Clock, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WD = ["Δευ", "Τρί", "Τετ", "Πέμ", "Παρ", "Σάβ", "Κυρ"];
const WD_FULL = ["Δευτέρα", "Τρίτη", "Τετάρτη", "Πέμπτη", "Παρασκευή", "Σάββατο", "Κυριακή"];
const MONTHS = ["Ιαν", "Φεβ", "Μαρ", "Απρ", "Μαΐ", "Ιουν", "Ιουλ", "Αυγ", "Σεπ", "Οκτ", "Νοε", "Δεκ"];
const GRADES = ["Γ' Γυμνασίου", "Α' Λυκείου", "Β' Λυκείου", "Γ' Λυκείου", "Απόφοιτος/η"];
const inputCls = "w-full h-11 px-3 border border-border rounded-sm bg-white text-sm focus:outline-none focus:border-primary";
const initial = { name: "", email: "", phone: "", student_grade: "", notes: "" };

const fmtDate = (iso) => { const d = new Date(iso + "T00:00:00"); return `${WD_FULL[(d.getDay() + 6) % 7]} ${d.getDate()} ${MONTHS[d.getMonth()]}`; };

export default function Booking() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const [day, setDay] = useState(null);
  const [time, setTime] = useState(null);
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const load = () => axios.get(`${API}/appointments/slots`).then(({ data }) => setData(data)).catch(() => setData({ days: [], duration: 45 }));
  useEffect(() => { load(); }, []);

  const PER = 7;
  const days = data?.days || [];
  const visible = useMemo(() => days.slice(page * PER, page * PER + PER), [days, page]);
  const selected = days.find((d) => d.date === day);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!day || !time) return toast.error("Επιλέξτε ημέρα και ώρα.");
    if (!form.name || !form.email || !form.phone) return toast.error("Συμπληρώστε όνομα, email και τηλέφωνο.");
    setLoading(true);
    try {
      const { data: appt } = await axios.post(`${API}/appointments`, { ...form, date: day, time });
      setDone(appt);
      toast.success("Το ραντεβού κλείστηκε!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Κάτι πήγε στραβά. Δοκιμάστε ξανά.");
      load(); setTime(null);
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div data-testid="booking-success" className="bg-white border-2 border-emerald-600 rounded-sm p-8 text-center">
        <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-4" strokeWidth={1.5} />
        <h3 className="font-heading font-bold text-2xl">Το ραντεβού σας επιβεβαιώθηκε!</h3>
        <p className="mt-3 text-muted-foreground">
          <strong className="text-foreground">{fmtDate(done.date)}, {done.time}</strong> ({data.duration}') — 25ης Μαρτίου 64, Πετρούπολη.
          {done.email_sent ? " Σας στείλαμε email επιβεβαίωσης." : " Θα επικοινωνήσουμε για επιβεβαίωση."}
        </p>
        <button data-testid="booking-again" onClick={() => { setDone(null); setDay(null); setTime(null); setForm(initial); load(); }} className="mt-6 font-mono-accent text-xs uppercase tracking-widest text-primary hover:underline">Νέο ραντεβού</button>
      </div>
    );
  }

  return (
    <div id="booking" data-testid="booking-widget" className="bg-white border border-border rounded-sm p-5 md:p-8 scroll-mt-28">
      <div className="flex items-center gap-2 mb-1">
        <CalendarCheck className="h-5 w-5 text-primary" strokeWidth={1.5} />
        <h3 className="font-heading font-bold text-lg">Κλείσε ραντεβού προσανατολισμού</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-6">Δωρεάν συνάντηση {data?.duration || 45}' με γονέα & μαθητή. Διάλεξε ημέρα και ώρα — θα λάβεις email επιβεβαίωσης.</p>

      <div className="flex items-center justify-between mb-3">
        <span className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground">1. Ημέρα</span>
        <div className="flex gap-1">
          <button data-testid="booking-prev" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="h-8 w-8 flex items-center justify-center border border-border rounded-sm disabled:opacity-30 hover:border-primary"><ChevronLeft className="h-4 w-4" /></button>
          <button data-testid="booking-next" onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * PER >= days.length} className="h-8 w-8 flex items-center justify-center border border-border rounded-sm disabled:opacity-30 hover:border-primary"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 mb-6" data-testid="booking-days">
        {!data && <div className="col-span-full text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Φόρτωση…</div>}
        {visible.map((d) => {
          const free = d.slots.filter((s) => s.available).length;
          const dt = new Date(d.date + "T00:00:00");
          const active = day === d.date;
          return (
            <button
              key={d.date}
              data-testid={`booking-day-${d.date}`}
              disabled={free === 0}
              onClick={() => { setDay(d.date); setTime(null); }}
              className={`flex flex-col items-center py-2.5 border rounded-sm transition-colors disabled:opacity-35 ${active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
            >
              <span className="font-mono-accent text-[10px] uppercase tracking-widest">{WD[d.weekday]}</span>
              <span className="font-heading font-extrabold text-xl leading-tight">{dt.getDate()}</span>
              <span className={`font-mono-accent text-[9px] ${active ? "text-white/70" : "text-muted-foreground"}`}>{MONTHS[dt.getMonth()]} · {free} ώρες</span>
            </button>
          );
        })}
      </div>

      {selected && (
        <>
          <div className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground mb-3">2. Ώρα — {fmtDate(selected.date)}</div>
          <div className="flex flex-wrap gap-2 mb-6" data-testid="booking-times">
            {selected.slots.map((s) => (
              <button
                key={s.time}
                data-testid={`booking-time-${s.time}`}
                disabled={!s.available}
                onClick={() => setTime(s.time)}
                className={`inline-flex items-center gap-1.5 h-10 px-4 border rounded-sm font-mono-accent text-xs transition-colors disabled:opacity-35 disabled:line-through ${time === s.time ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
              >
                <Clock className="h-3.5 w-3.5" /> {s.time}
              </button>
            ))}
          </div>
        </>
      )}

      {time && (
        <form onSubmit={submit} data-testid="booking-form" className="border-t border-border pt-6">
          <div className="font-mono-accent text-[10px] uppercase tracking-widest text-muted-foreground mb-3">3. Στοιχεία</div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="booking-name" name="name" value={form.name} onChange={onChange} className={inputCls} placeholder="Ονοματεπώνυμο γονέα *" />
            <input data-testid="booking-phone" name="phone" value={form.phone} onChange={onChange} className={inputCls} placeholder="Τηλέφωνο *" />
            <input data-testid="booking-email" name="email" type="email" value={form.email} onChange={onChange} className={inputCls} placeholder="Email *" />
            <select data-testid="booking-grade" name="student_grade" value={form.student_grade} onChange={onChange} className={inputCls}>
              <option value="">Τάξη μαθητή</option>
              {GRADES.map((g) => <option key={g}>{g}</option>)}
            </select>
            <textarea data-testid="booking-notes" name="notes" value={form.notes} onChange={onChange} rows={2} className={`${inputCls} h-auto py-2 sm:col-span-2 resize-none`} placeholder="Σημειώσεις (π.χ. έχουμε κάνει τα τεστ ΕΟΠΠΕΠ)" />
          </div>
          <button data-testid="booking-submit" disabled={loading} className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-8 bg-primary text-primary-foreground font-mono-accent text-xs uppercase tracking-widest rounded-sm hover:bg-accent transition-colors disabled:opacity-60">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Επιβεβαίωση ραντεβού — {fmtDate(day)} {time}
          </button>
        </form>
      )}
    </div>
  );
}
